import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link as RouterLink } from "react-router";
import CityscapeBackdrop from "src/components/CityscapeBackdrop";
import { useT } from "src/i18n";

export default function HeroSection({ greeting = null }) {
    const theme = useTheme();
    const t = useT();
    const MICROCOPY = [
        { Icon: BoltIcon, text: t("landing.hero.microcopy.fast") },
        { Icon: CheckCircleIcon, text: t("landing.hero.microcopy.free") },
        { Icon: CheckCircleIcon, text: t("landing.hero.microcopy.noCommitment") },
    ];
    // Single canonical headline + subline. The headline is the page's <h1> and is
    // intentionally STABLE — no per-load randomisation — so the most important SEO /
    // recognition signal never churns and matches the crawlable prerender <h1> in
    // index.html exactly. `lead` renders in the base hero color, `accent` in
    // theme.custom.heroAccent — the punchy value-prop phrase stays in `accent`. SEO
    // invariant: the headline must contain the primary keyword (betoni + pumppaus /
    // "betonipumppaus"). If the prerender <h1> changes, change this to match it.
    const HERO_TAGLINE = {
        lead: t("landing.hero.taglineLead"),
        accent: t("landing.hero.taglineAccent"),
        sub: t("landing.hero.taglineSub"),
    };
    const easing = theme.custom?.wizard?.motion?.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)";
    // First-load hero choreography — the brand's one orchestrated moment. Jerry
    // settles into the glow, then the message and the amber CTA rise into place in
    // sequence. Transform-only and self-contained per element: opacity is never
    // touched, so nothing (least of all the SEO <h1>) is gated invisible — the rise
    // only enhances an already-visible default, and if a keyframe ever failed to
    // run, the content is still fully visible and in place. No bounce (ease-out-quint).
    const reducedMotionOff = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };
    const heroRiseSx = (delay) => ({
        animation: `bjHeroRise 400ms ${easing} ${delay}ms backwards`,
        "@keyframes bjHeroRise": {
            from: { transform: "translateY(9px)" },
            to: { transform: "translateY(0)" },
        },
        ...reducedMotionOff,
    });
    const heroMascotSx = {
        animation: `bjHeroMascot 460ms ${easing} backwards`,
        "@keyframes bjHeroMascot": {
            from: { transform: "rotate(-5deg) translateY(10px)" },
            to: { transform: "rotate(-3deg) translateY(0)" },
        },
        ...reducedMotionOff,
    };
    const tagline = HERO_TAGLINE;
    const heroTextColor = theme.custom?.heroTextColor || "text.primary";
    const heroAccent = theme.custom?.heroAccent;
    const mascotFilter = theme.custom?.mascotFilter || "none";
    const backdropGradient = theme.custom?.heroBackdropGradient;

    return (
        <Box
            sx={{
                py: { xs: 2, md: 3 },
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                position: "relative",
                backgroundImage: backdropGradient && backdropGradient !== "none" ? backdropGradient : "none",
            }}
        >
            <CityscapeBackdrop />
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 3, md: 6 }}
                sx={{ alignItems: "center", justifyContent: "center", width: "100%", minWidth: 0, position: "relative", zIndex: 1 }}
            >
                <Box
                    component="img"
                    src="/mascot/jerry.webp"
                    alt={t("landing.hero.mascotAlt")}
                    sx={{
                        width: { xs: 200, sm: 240, md: 300 },
                        height: "auto",
                        flexShrink: 0,
                        transform: "rotate(-3deg)",
                        filter: mascotFilter === "none" ? "drop-shadow(0 8px 18px rgba(0,0,0,0.28))" : `${mascotFilter} drop-shadow(0 8px 18px rgba(0,0,0,0.28))`,
                        ...heroMascotSx,
                    }}
                />
                <Stack
                    spacing={2.5}
                    sx={{ alignItems: { xs: "center", md: "flex-start" },
                        textAlign: { xs: "center", md: "left" },
                        width: "100%",
                        maxWidth: { xs: "100%", md: 520 },
                        minWidth: 0,
                    }}
                >
                    {greeting && (
                        <Typography
                            variant="h6"
                            sx={{
                                color: heroTextColor,
                                opacity: 0.9,
                                fontWeight: 400,
                                ...heroRiseSx(50),
                            }}
                        >
                            {t("landing.hero.greeting", { name: greeting })}
                        </Typography>
                    )}
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: heroTextColor,
                            lineHeight: 1.15,
                            fontWeight: 700,
                            fontSize: { xs: "1.85rem", sm: "2.2rem", md: "2.4rem" },
                            textWrap: "balance",
                            ...heroRiseSx(90),
                        }}
                    >
                        {tagline.lead}
                        <Box component="span" sx={heroAccent ? { color: heroAccent } : undefined}>
                            {tagline.accent}
                        </Box>
                    </Typography>
                    <Typography
                        variant="h6"
                        component="p"
                        sx={{
                            color: heroTextColor,
                            lineHeight: 1.3,
                            fontWeight: 400,
                            opacity: 0.92,
                            ...heroRiseSx(140),
                        }}
                    >
                        {tagline.sub}
                    </Typography>
                    <Stack spacing={1.5} sx={{
                        alignItems: { xs: "center", md: "flex-start" },
                        // The amber work-lamp arrives last — the action lands the choreography.
                        ...heroRiseSx(190),
                    }}>
                        <Button
                            size="large"
                            variant="contained"
                            color="warning"
                            component={RouterLink}
                            to="/tilaa"
                            sx={{
                                px: { xs: 5, md: 7 },
                                py: { xs: 1.75, md: 2.25 },
                                fontSize: { xs: "1.15rem", md: "1.3rem" },
                            }}
                        >
                            {t("common.nav.requestQuoteCta")}
                        </Button>
                        <Stack
                            direction="row"
                            spacing={{ xs: 1.5, sm: 2.5 }}
                            sx={{ alignItems: "center", justifyContent: { xs: "center", md: "flex-start" }, flexWrap: "wrap", rowGap: 0.5 }}
                        >
                            {MICROCOPY.map(({ Icon, text }) => (
                                <Stack key={text} direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <Icon sx={{ color: heroTextColor, fontSize: 18 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: heroTextColor, fontWeight: 600 }}
                                    >
                                        {text}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
}
